import { useEffect, useState } from 'react'
import {
  ApplicationItemStatus,
  useAffiliateApplication,
} from './useAffiliateAplication'
import { useSettings } from 'components/Settings'
import { useMyActiveSubscriptions } from './subscriptions'
import { useDispatch, useSelector } from 'react-redux'
import {
  SubscriptionPackageType,
  SubscriptionStatus,
} from 'services/programming'
import {
  updateShowBanner,
  updateBannerMessage,
  updateBannerButton,
  updateBannerType,
} from 'store/slices/banner'
import { getMyAffiliates } from 'store/selectors'

export type UseBannerResponse = {
  showingBanner: boolean
}

export default function useBanners(): UseBannerResponse {
  const dispatch = useDispatch()
  const { myAffiliates } = useSelector(getMyAffiliates)
  const applicationStatusQuery = useAffiliateApplication()
  const settings = useSettings((s) => s.data)
  const subscriptions = useMyActiveSubscriptions()
  const askSubscriptions = subscriptions?.filter(
    (s) => s.package.name === SubscriptionPackageType.ATK_STARTER,
  )
  const latestAskSubscription = askSubscriptions?.reduce((latest, current) => {
    if (!latest) return current
    return new Date(current?.activationDate) > new Date(latest?.activationDate)
      ? current
      : latest
  }, null)

  const latestAffiliate = myAffiliates?.reduce((latest, current) => {
    if (!latest) return current
    return new Date(current?.affiliateEffectiveDate) >
      new Date(latest?.affiliateEffectiveDate)
      ? current
      : latest
  }, null)

  /*
    NOTE: If the latest ask sub is newer than the user's latest affiliate,
          then we can infer that this is a new subscription and we should show ask
          related banners
  */
  const newAsk =
    new Date(latestAskSubscription?.activationDate) >
    new Date(latestAffiliate?.affiliateEffectiveDate ?? null)
  let bannerText = ''
  let ctaText = ''
  let ctaUrl = ''
  let bannerType = ''
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    const banners = subscriptions && applicationStatusQuery.isSuccess && newAsk

    if (banners) {
      const subscriptionExpirationInDays = Math.floor(
        (new Date(latestAskSubscription?.expiryDate)?.getTime() -
          new Date().getTime()) /
          (1000 * 60 * 60 * 24),
      )

      if (latestAskSubscription?.status === SubscriptionStatus.active) {
        if (
          applicationStatusQuery &&
          applicationStatusQuery.data?.application?.status ===
            ApplicationItemStatus.DEFAULT
        ) {
          setShowBanner(true)
          bannerText = settings.bannerMessages.askNotApplied.text
          ctaText = settings.bannerMessages.askNotApplied.cta.text
          ctaUrl = settings.bannerMessages.askNotApplied.cta.url
          bannerType = 'info'
        }

        if (
          applicationStatusQuery &&
          applicationStatusQuery.data?.application?.status !==
            ApplicationItemStatus.DEFAULT
        ) {
          setShowBanner(true)
          bannerText = settings.bannerMessages.askReviewApplication.text
          ctaText = settings.bannerMessages.askReviewApplication.cta.text
          ctaUrl = settings.bannerMessages.askReviewApplication.cta.url
          bannerType = 'info'
        }
      }
      if (subscriptionExpirationInDays <= 30) {
        setShowBanner(true)

        if (
          applicationStatusQuery.data?.application?.status ===
          ApplicationItemStatus.DEFAULT
        ) {
          bannerText = settings.bannerMessages.starterKitExpiry.text.replace(
            '[X]',
            subscriptionExpirationInDays === 1
              ? '1 day'
              : `${subscriptionExpirationInDays} days`,
          )
          ctaText = settings.bannerMessages.starterKitExpiry.cta.text
          ctaUrl = settings.bannerMessages.starterKitExpiry.cta.url
          bannerType = settings.bannerMessages.starterKitExpiry.bannerType
        } else {
          bannerText =
            settings.bannerMessages.askAppliedAccessExpiring.text.replace(
              '[X]',
              subscriptionExpirationInDays === 1
                ? '1 day'
                : `${subscriptionExpirationInDays} days`,
            )
          ctaText = settings.bannerMessages.askAppliedAccessExpiring.cta.text
          ctaUrl = settings.bannerMessages.askAppliedAccessExpiring.cta.url
          bannerType =
            settings.bannerMessages.askAppliedAccessExpiring.bannerType
        }
      }

      dispatch(updateShowBanner(showBanner))
      dispatch(updateBannerMessage(bannerText))
      dispatch(updateBannerButton({ text: ctaText, url: ctaUrl }))
      dispatch(updateBannerType(bannerType))
    }
  }, [subscriptions, applicationStatusQuery, myAffiliates])

  return {
    showingBanner: showBanner,
  }
}
